/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './workspace-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {
  addWorkspaceWidget,
  buildWorkspaceGridsterOpts,
  initWorkspaceModel,
  prepareWorkspaceWidgetsForSubmit,
  removeWorkspaceWidget,
  setupWorkspaceWidgetDefaults,
  workspaceWhichFields
} from './workspace-commons';
import type {WorkspaceGridsterResource} from './workspace-gridster-item.component';
import {WorkspaceWidgetModalComponent} from './workspace-widget-modal.component';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {WorkspaceRegistryService} from '../../components/workspace-registry.service';
import {AngularViewRefreshService} from '../common/util/angular-view-refresh.service';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-workspace-edit-page',
  template
})
export class WorkspaceEditPageComponent extends AdminResourceEditPageBase {
  hasNetworkPermission = false;
  allTeams: any[] = [];
  gridsterOpts = buildWorkspaceGridsterOpts(true);

  readonly gridsterResource: WorkspaceGridsterResource = {
    deleteWidget: (widget) => this.deleteWidget(widget),
    changeType: (widget) => this.changeType(widget),
    whichFields: (fieldType, widget) => this.whichFields(fieldType, widget),
    addComparisons: (widget) => this.addComparisons(widget),
    removeComparisons: (widget, index) => this.removeComparisons(widget, index),
    markDirty: () => this.markExternalFormDirty()
  };

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(WorkspaceRegistryService) private readonly WorkspaceRegistry: any,
    private readonly $state: StateService,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly angularModalService: AngularModalService,
    private readonly viewRefresh: AngularViewRefreshService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
  }

  compareTeams(a: any, b: any): boolean {
    return a?.id === b?.id;
  }

  protected afterInitEditingModel(): void {
    this.hasNetworkPermission = this.LocalUserPermissions.hasNetworkPermission('MANAGE_NETWORK_DASHBOARDS');
    initWorkspaceModel(
      this.editingModel,
      this.hasNetworkPermission,
      this.LocalNetworkProfile.isPureMarketplacePlatform()
    );
    if (this.editingModel.type === 'TEAM') {
      this.loadTeams();
    }
    this.setupWidgetDefaults();
    this.gridsterOpts = buildWorkspaceGridsterOpts(true, () => this.markExternalFormDirty());
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (saveObj) {
      this.WorkspaceRegistry.update(saveObj);
      setTimeout(() => {
        this.$state.go('app.wor.workspaces.workspace', {id: saveObj.id});
      }, 0);
    }
  }

  protected onBeforeSubmit(): void {
    prepareWorkspaceWidgetsForSubmit(
      this.editingModel.data.widgets,
      this.adnTimeShifter
    );
  }

  updateType(): void {
    if (this.editingModel.type === 'TEAM') {
      this.loadTeams();
    } else {
      this.editingModel.team = undefined;
    }
    this.markExternalFormDirty();
  }

  changeType(widget: any): void {
    widget.objIds = [];
    this.markExternalFormDirty();
  }

  whichFields(fieldType: string, widget: any): boolean {
    return workspaceWhichFields(fieldType, widget);
  }

  addComparisons(widget: any): void {
    widget.fieldComparisons = widget.fieldComparisons || [];
    widget.fieldComparisons.push({});
    this.markExternalFormDirty();
  }

  removeComparisons(widget: any, index: number): void {
    _.pullAt(widget.fieldComparisons, index);
    this.markExternalFormDirty();
  }

  selectWidgets(): void {
    const modalRef = this.angularModalService.show(WorkspaceWidgetModalComponent, {
      class: 'modal-lg adnDialog'
    });
    (modalRef.content as WorkspaceWidgetModalComponent).onSaved.subscribe((selections: any[]) => {
      if (!selections?.length) {
        return;
      }
      const added = _.map(selections, (sel) => addWorkspaceWidget(this.editingModel.data.widgets, sel));
      // Only hydrate the new tiles — re-running defaults on every existing widget
      // was a multi-frame pause before paint.
      setupWorkspaceWidgetDefaults(
        added,
        (key) => this.translate.instant(key),
        this.LocalNetworkProfile,
        this.adnTimeShifter
      );
      this.markExternalFormDirty();
      // markExternalFormDirty no-ops when already dirty and skips refresh — always
      // schedule so zoneless CD paints the new gridster items immediately.
      this.viewRefresh.schedule();
    });
  }

  deleteWidget(widget: any): void {
    removeWorkspaceWidget(this.editingModel.data.widgets, widget);
    this.markExternalFormDirty();
    this.viewRefresh.schedule();
  }

  private setupWidgetDefaults(): void {
    setupWorkspaceWidgetDefaults(
      this.editingModel.data.widgets,
      (key) => this.translate.instant(key),
      this.LocalNetworkProfile,
      this.adnTimeShifter
    );
  }

  private loadTeams(): void {
    const perms = this.LocalUserPermissions.getAllVisPermissions();
    const teamTypes: string[] = [];
    if (perms.AX_ADVERTISER_ADMIN === true || perms.AX_ADVERTISER_TEAM_ADMIN === true) {
      teamTypes.push('AX_ADVERTISER');
    }
    if (perms.AX_PUBLISHER_ADMIN === true || perms.AX_PUBLISHER_TEAM_ADMIN === true) {
      teamTypes.push('AX_PUBLISHER');
    }
    const params = {minimal: true, teamTypes: teamTypes.join(';')};
    this.Team.query(params).$promise.then((page: any) => {
      this.allTeams = page.results || [];
    });
  }
}
